import React from 'react';
import { useDispatch } from 'react-redux';
import { type Dispatch } from 'redux';
import { type TFunction } from 'i18next';
import { useIntl, type IntlShape } from 'react-intl';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import type { IconButtonOwnProps } from '@mui/material';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import useSettings from '../hooks/useSettings';
import useMountEffect from '../hooks/useMountEffect';
import useUserData from '../hooks/useUserData';
import useIsModuleDisabled, { type IsModuleDisabledType } from '../hooks/useIsModuleDisabled';
import Icon from '../Icon';

interface ButtonArgs {
	/** The Redux dispatcher function */
	dispatch: Dispatch<any>;
	/** The application settings */
	sysSettings: any;
	/** The user data */
	userData: any;
	/** The function used to check if a module is disabled */
	isModuleDisabled: IsModuleDisabledType;
	/** The authorizer */
	authorizer: any;
	/** The function used to handle internationalization */
	intl: IntlShape;
	/** The function used to handle translations */
	t: TFunction;
}

export interface MultiButtonOptionType<T> {
	name: string;
	icon: string;
	iconAdd?: boolean;
	color?: string;
	hidden?: (buttonArgs: T & ButtonArgs) => boolean;
	disabled?: (buttonArgs: T & ButtonArgs) => boolean;
	action: (buttonArgs: T & ButtonArgs) => void;
}

interface MultiButtonMenuListItemProps<T> {
	option: MultiButtonOptionType<T>;
	buttonArgs: T & ButtonArgs;
	closeMenu: VoidFunction;
}

export interface MultiButtonComponentProps {
	anchorRef: React.RefObject<HTMLButtonElement>;
	onClick: VoidFunction;
}

export interface MultiButtonProps<T> {
	component?: React.FC<MultiButtonComponentProps>;
	items: MultiButtonOptionType<T>[];
	buttonProps: T;
	size?: 'small' | 'medium' | 'large';
	edge?: IconButtonOwnProps['edge'];
}

function MultiButtonMenuListItem<T>({ option, buttonArgs, closeMenu }: MultiButtonMenuListItemProps<T>) {
	const { t } = useSettings();

	return (
		<MenuItem
			disabled={Boolean(option.disabled?.(buttonArgs))}
			onClick={() => {
				option.action(buttonArgs);
				closeMenu();
			}}
		>
			<ListItemIcon style={{ color: option.color || '' }}>
				{option.iconAdd ? (
					<>
						<Icon className="my-4 mr-2" icon="plus" size={16} />
						<Icon className="mr-8" icon={option.icon} />
					</>
				) : (
					<Icon icon={option.icon} />
				)}
			</ListItemIcon>
			<ListItemText
				slotProps={{
					primary: {
						sx: {
							color: option.color || '',
							fontWeight: option.color ? 500 : null
						}
					}
				}}
			>
				{t(option.name)}
			</ListItemText>
		</MenuItem>
	);
}

export default function MultiButton<T>({ component: Component, items, buttonProps, size, edge }: MultiButtonProps<T>) {
	const dispatch = useDispatch();
	const { sysSettings, t } = useSettings();
	const userData = useUserData();
	const intl = useIntl();
	const isModuleDisabled = useIsModuleDisabled();
	const authorizer = React.useContext(AuthorizerContext);

	const buttonArgs: T & ButtonArgs = {
		...buttonProps,
		dispatch,
		sysSettings,
		isModuleDisabled,
		authorizer,
		userData,
		intl,
		t
	};

	const maxMenuItems = 6;
	const anchorRef = React.useRef<HTMLButtonElement>(null);

	const [openMenu, setOpenMenu] = React.useState(false);

	function handleClose(event) {
		if (anchorRef.current && anchorRef.current.contains(event.target)) return;
		setOpenMenu(false);
		event.preventDefault();
	}

	const [visibleItems, setVisibleItems] = React.useState<MultiButtonOptionType<T>[]>([]);

	useMountEffect(() => {
		const tmp: MultiButtonOptionType<T>[] = [];
		items.forEach(item => {
			if (!item.hidden || !item.hidden(buttonArgs)) tmp.push(item);
		});
		setVisibleItems(tmp);
	});

	return (
		visibleItems.length > 0 && (
			<>
				{Component ? (
					<Component anchorRef={anchorRef} onClick={() => setOpenMenu(true)} />
				) : (
					<Tooltip title={t('VIEW_ITEM_BUTTON_MORE')} arrow>
						<IconButton ref={anchorRef} size={size} edge={edge} onClick={() => setOpenMenu(true)}>
							<Icon icon="dots-vertical" />
						</IconButton>
					</Tooltip>
				)}
				<Menu
					anchorEl={anchorRef.current}
					keepMounted
					open={openMenu}
					onClose={handleClose}
					slotProps={{
						paper: { style: { maxHeight: 48 * maxMenuItems } },
						list: { dense: true }
					}}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right'
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right'
					}}
				>
					{visibleItems.map(option => (
						<MultiButtonMenuListItem
							key={option.name}
							buttonArgs={buttonArgs}
							option={option}
							closeMenu={() => setOpenMenu(false)}
						/>
					))}
				</Menu>
			</>
		)
	);
}

// export default React.memo(MultiButton);
